import {
  Box,
  type BoxProps,
  Flex,
  Text,
  Typography,
  useUiKitComponents,
} from "@qws-org/ui-kit";
import type { FC } from "react";
import { useState } from "react";
import { useLocation } from "react-router";

import type {
  PagePreview,
  TextWithAnchors as TextWithAnchorsSdk,
} from "~/core";
import { InfoPageList } from "~/features/info-page-list/InfoPageList";

export type TextBlockProps = {
  data: TextWithAnchorsSdk;
  pages?: Array<PagePreview>;
  getPageUrl: (page: PagePreview) => string;
  isActive: (pageItem: PagePreview) => boolean;
} & Partial<
  Omit<BoxProps, "display" | "as" | "children" | "className" | "onClick">
>;

export const TextWithAnchors: FC<TextBlockProps> = ({
  data,
  pages,
  getPageUrl,
  isActive,
  ...rest
}) => {
  const { Link } = useUiKitComponents();
  const location = useLocation();
  const [activeItem, setActiveItem] = useState<string | null>(null);

  const currentLang = location.pathname.split("/")[1] || "";
  const pathWithoutLang = location.pathname.replace(`/${currentLang}`, "");

  return (
    <Flex
      flexDirection="column-reverse"
      p={{ px: 24 }}
      gap={24}
      {...rest}
      d={{
        flexDirection: "row",
        justify: "space-between",
        p: { px: 0 },
        gap: 105,
        ...rest.d,
      }}
    >
      <Text
        minWidth={312}
        maxWidth="100%"
        width="100%"
        size="medium"
        content={data?.text ?? ""}
      />

      <Flex flexDirection="column" gap={20}>
        {pages && data.showRelatedPages && (
          <InfoPageList
            data={pages}
            isActive={isActive}
            getPageUrl={getPageUrl}
            width="100%"
            d={{ width: 400, m: { mt: 0 } }}
            minWidth={312}
          />
        )}
        {data.navigation && (
          <Flex flexDirection="column">
            {data.navigation.map((item) => {
              const isItemActive =
                item?.text_with_anchors_navigation_id?.anchor_id === activeItem;
              return (
                <Link
                  key={item?.id}
                  onClick={() =>
                    setActiveItem(
                      item?.text_with_anchors_navigation_id?.anchor_id ?? null,
                    )
                  }
                  href={
                    `${pathWithoutLang}#user-content-${item?.text_with_anchors_navigation_id?.anchor_id}` ??
                    ""
                  }
                >
                  <Box
                    as="span"
                    p={{ px: 16, py: 12 }}
                    md={{ p: { px: 32, py: 12 } }}
                    bg={
                      isItemActive
                        ? "surface-neutral-default"
                        : "surface-neutral-light"
                    }
                    className={`${isItemActive ? "border-l-4 border-[var(--colors-border-selected)]" : ""}`}
                  >
                    <Typography
                      size="medium"
                      as="span"
                      md={{
                        fontSize: "body.desktop.large",
                        lineHeight: "body.desktop.large",
                        fontWeight: "desktop.semibold",
                      }}
                    >
                      {item?.text_with_anchors_navigation_id?.title}
                    </Typography>
                  </Box>
                </Link>
              );
            })}
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};
